import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Select } from "@nutrayan/ui";
import { listTenants } from "@/lib/tenant-api";
import { useTenantOverride } from "@/lib/tenant-override";
import { useClaims } from "@/lib/use-claims";
import { isBootstrapLevel } from "@/lib/permissions";

/**
 * Header tenant switcher for BOOTSTRAP_SUPER admins (BLK-API-015). Picking a
 * tenant sends `X-Tenant-Override` on every request (via the api-client) so all
 * tenant-scoped pages show that tenant's data; "Platform" clears the override.
 * Hidden for every other admin — they are already scoped to their own tenant.
 */
export function TenantSwitcher() {
  const { adminLevel } = useClaims();
  const bootstrap = isBootstrapLevel(adminLevel);
  const qc = useQueryClient();
  const override = useTenantOverride();

  const tenants = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: listTenants,
    enabled: bootstrap,
    staleTime: 5 * 60 * 1000,
  });

  if (!bootstrap) return null;

  const onChange = (tenantId: string) => {
    const tenant = (tenants.data ?? []).find((t) => t.id === tenantId);
    override.setTenant(tenant ? { id: tenant.id, code: tenant.code } : null);
    // Every tenant-scoped dataset (orders, catalog, dashboards…) changes at once.
    void qc.invalidateQueries();
  };

  return (
    <label className="hidden items-center gap-2 md:flex">
      <span className="text-caption text-muted whitespace-nowrap">Tenant</span>
      <Select
        aria-label="Administer tenant"
        value={override.tenantId ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-44"
      >
        <option value="">Platform (global)</option>
        {(tenants.data ?? []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.displayName ?? t.id}
          </option>
        ))}
      </Select>
    </label>
  );
}
